// Single home for supplier-order number ordering.
//
// A generated supplier-order number is `ZD/RRRR/MM/NR` (e.g. `ZD/2026/09/79`).
// Compared as plain text, `ZD/2026/10/3` lands before `ZD/2026/09/79`, so the
// list stops reading as "newest first". The parts are therefore normalised to
// a zero-padded `PREFIX-YYYY-MM-NNNNNN` key, mirroring the customer-side
// semantics in `pages/orderTableUtils.ts` (`getOrderNumberSortParts`), where
// the customer format is `NR/MM/RRRR`.
//
// Manually numbered orders keep whatever the operator typed; like on the
// customer table they are grouped after the generated period numbers in both
// sort directions, so switching direction never interleaves the two kinds.

export type OrderNumberSortParts = {
  isPeriodNumber: boolean;
  value: string;
};

export const getSupplierOrderNumberSortParts = (
  displayNumber?: string | null
): OrderNumberSortParts => {
  const trimmed = (displayNumber || '').trim();
  const parts = trimmed.split('/').map((part) => part.trim());

  if (parts.length === 4) {
    const [prefix, year, month, sequence] = parts;

    if (
      prefix &&
      /^\d+$/.test(year) &&
      /^\d+$/.test(month) &&
      /^\d+$/.test(sequence)
    ) {
      return {
        isPeriodNumber: true,
        value: [
          prefix.toLocaleUpperCase('pl'),
          year.padStart(4, '0'),
          month.padStart(2, '0'),
          sequence.padStart(6, '0'),
        ].join('-'),
      };
    }
  }

  return { isPeriodNumber: false, value: trimmed };
};

export const compareSupplierOrderNumbers = (
  leftDisplayNumber: string | null | undefined,
  rightDisplayNumber: string | null | undefined,
  direction: 1 | -1
): number => {
  const left = getSupplierOrderNumberSortParts(leftDisplayNumber);
  const right = getSupplierOrderNumberSortParts(rightDisplayNumber);

  if (left.isPeriodNumber !== right.isPeriodNumber) {
    return left.isPeriodNumber ? -1 : 1;
  }

  return (
    left.value.localeCompare(right.value, 'pl', {
      numeric: true,
      sensitivity: 'base',
    }) * direction
  );
};
